import React, { useState, useEffect, useCallback } from 'react';
import { getCreditScore } from '../utils/creditScore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import ExportToolbar from '../components/ExportToolbar';
import { exportCSV, exportJSON, todayStr } from '../utils/exportUtils';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Solid pills — secondary-palette scheme (see constants/statusColors.js).
const STATUS_DISPLAY = {
  approved:         { label: 'Approved',        bg: '#acf44e', color: '#1a1a1a' },  // Doorly green
  home_shopping:    { label: 'Shopping',         bg: '#7C58F5', color: '#ffffff' },  // Violet
  offer_requested:  { label: 'Offer Requested',  bg: '#FF7A1A', color: '#1a1a1a' },  // Orange
  option_period:    { label: 'Option Period',    bg: '#FFDD86', color: '#1a1a1a' },  // Tan
  closing:          { label: 'Closing',          bg: '#14B8A6', color: '#1a1a1a' },  // Teal
};

function fmt$(v) {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '—';
  return '$' + Math.round(n).toLocaleString();
}
function fmtPct(v, decimals = 1) {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toFixed(decimals) + '%';
}

function StatusBadge({ status }) {
  const s = STATUS_DISPLAY[status] || { label: status?.replace(/_/g, ' ') || '—', bg: '#f9fafb', color: '#374151' };
  return (
    <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 9px', borderRadius: '9999px',
      backgroundColor: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function ltvColor(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return '#374151';
  if (n < 80) return '#acf44e';
  if (n <= 90) return '#d97706';
  return '#ff082f';
}
function dtiColor(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return '#374151';
  if (n < 43) return '#acf44e';
  if (n <= 50) return '#d97706';
  return '#ff082f';
}

const STATUSES = [
  { key: '', label: 'All' },
  { key: 'approved', label: 'Approved' },
  { key: 'conditional_approval', label: 'Conditional' },
  { key: 'home_shopping', label: 'Shopping' },
  { key: 'offer_requested', label: 'Offer Requested' },
  { key: 'offer_submitted', label: 'Offer Submitted' },
  { key: 'offer_accepted', label: 'Offer Accepted' },
  { key: 'option_period', label: 'Option Period' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'loan_docs', label: 'Loan Docs' },
  { key: 'closing_disclosures', label: 'CD Issued' },
  { key: 'closing', label: 'Closing' },
  { key: 'clear_to_close', label: 'Clear to Close' },
  { key: 'closed', label: 'Closed' },
  { key: 'funded', label: 'Funded' },
];

const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const th = { padding: '10px 12px', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' };
const td = { padding: '11px 12px', fontSize: '13px', color: '#374151', borderBottom: '1px solid #f9fafb', whiteSpace: 'nowrap' };
const tdTotal = { padding: '11px 12px', fontSize: '13px', fontWeight: '700', color: '#111827', borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb', whiteSpace: 'nowrap' };

export default function AdminLoanTape() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilters, setStatusFilters] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toggleStatus = (key) => {
    setStatusFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilters.size > 0) params.statuses = [...statusFilters].join(',');
      const res = await axios.get(`${API}/api/admin/borrowers/loan-tape`, { headers, params });
      setLoans(res.data.loans || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const loanTapeCSVColumns = [
    { label: 'Loan #', key: 'loan_number' },
    { label: 'Borrower', key: 'borrower_name' },
    { label: 'Address', accessor: (r) => (r.property_address || '').replace(/,/g, ' ') },
    { label: 'Type', key: 'property_type' },
    { label: 'Status', key: 'current_status' },
    { label: 'Doorly Purchase Price', key: 'doorly_purchase_price' },
    { label: 'Borrower Purchase Price', key: 'borrower_purchase_price' },
    { label: 'Down %', key: 'down_payment_pct' },
    { label: 'Loan Amount', key: 'loan_amount' },
    { label: 'LTV', key: 'ltv' },
    { label: 'DTI', key: 'dti_back' },
    { label: 'FICO', accessor: r => { const s = getCreditScore(r); return s.isActual ? `${s.display} (actual)` : (r.fico_range || ''); } },
    { label: 'Rate', key: 'interest_rate' },
    { label: 'RE Commission', key: 're_commission' },
    { label: 'Program Fee', key: 'program_fee' },
    { label: 'Total Revenue', key: 'total_revenue' },
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Loan Tape</h1>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>Active loans from Home Shopping through Closing</p>
          </div>
          <ExportToolbar
            disabled={loans.length === 0}
            onExportCSV={() => exportCSV(loans, loanTapeCSVColumns, `doorly-loan-tape-${todayStr()}`)}
            onExportJSON={() => exportJSON({ report: 'LoanTape', generated: new Date().toISOString(), summary, loans }, `doorly-loan-tape-${todayStr()}`)}
          />
        </div>

        {/* Filters */}
        <div style={{ ...card, marginBottom: '20px', padding: '14px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginRight: '4px' }}>STATUS</span>
            <button onClick={() => setStatusFilters(new Set())}
              style={{ padding: '5px 14px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                border: '1px solid',
                backgroundColor: statusFilters.size === 0 ? '#111827' : '#fff',
                color: statusFilters.size === 0 ? '#fff' : '#374151',
                borderColor: statusFilters.size === 0 ? '#111827' : '#e5e7eb' }}>
              All
            </button>
            {STATUSES.filter(s => s.key).map((s) => {
              const active = statusFilters.has(s.key);
              return (
              <button key={s.key} onClick={() => toggleStatus(s.key)}
                style={{ padding: '5px 14px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                  border: '1px solid',
                  backgroundColor: active ? '#111827' : '#fff',
                  color: active ? '#fff' : '#374151',
                  borderColor: active ? '#111827' : '#e5e7eb' }}>
                {s.label}
              </button>
              );
            })}
          </div>
        </div>

        {/* KPI summary row */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Loans', value: summary.count },
              { label: 'Purchase Volume', value: fmt$(summary.total_borrower_price) },
              { label: 'Loan Volume', value: fmt$(summary.total_loan_amount) },
              { label: 'Projected Revenue', value: fmt$(summary.total_revenue) },
              { label: 'Avg LTV', value: fmtPct(summary.avg_ltv) },
              { label: 'Avg DTI', value: fmtPct(summary.avg_dti_back) },
              { label: 'Avg Rate', value: fmtPct(summary.avg_interest_rate) },
            ].map((k) => (
              <div key={k.label} style={{ ...card, padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{k.label}</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>{k.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#ff082f', fontSize: '14px' }}>{error}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Loan #', 'Borrower', 'Address', 'Type', 'Status', 'Doorly Price', 'Borrower Price', 'Down %', 'Loan Amount', 'LTV', 'DTI', 'FICO', 'Rate', 'RE Commission', 'Program Fee', 'Total Revenue'].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loans.length === 0 ? (
                    <tr>
                      <td colSpan={18} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: '48px' }}>
                        No active loans in pipeline
                      </td>
                    </tr>
                  ) : loans.map((l) => (
                    <tr key={l.id} style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={td}>
                        <button onClick={() => navigate(`/admin/borrowers/${l.id}`)}
                          style={{ background: 'none', border: 'none', color: '#3a6ad9', fontWeight: '600', fontSize: '13px', cursor: 'pointer', padding: 0 }}>
                          {l.loan_number || '—'}
                        </button>
                      </td>
                      <td style={td}>{l.borrower_name || '—'}</td>
                      <td style={{ ...td, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.property_address || '—'}</td>
                      <td style={td}>
                        {l.property_type ? (
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '9999px', backgroundColor: '#f3f4f6', color: '#374151', fontWeight: '600' }}>
                            {l.property_type}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={td}>
                        <StatusBadge status={l.current_status} />
                        {['option_period', 'closing'].includes(l.current_status) && (
                          <span style={{ fontSize: '10px', background: '#f0fdf4', color: '#166534', padding: '1px 5px', borderRadius: '3px', marginLeft: '4px' }}>Actual</span>
                        )}
                        {['home_shopping', 'offer_requested'].includes(l.current_status) && (
                          <span style={{ fontSize: '10px', background: '#eff6ff', color: '#2854c0', padding: '1px 5px', borderRadius: '3px', marginLeft: '4px' }}>Est.</span>
                        )}
                      </td>
                      <td style={td}>{fmt$(l.doorly_purchase_price)}</td>
                      <td style={td}>{fmt$(l.borrower_purchase_price)}{l.price_type && <span style={{ fontSize: '9px', fontWeight: '700', marginLeft: '4px', padding: '1px 4px', borderRadius: '3px', background: l.price_type === 'contract' ? '#dbeafe' : l.price_type === 'accepted' ? '#d1fae5' : l.price_type === 'approved' ? '#fef3c7' : '#f3f4f6', color: l.price_type === 'contract' ? '#1e40af' : l.price_type === 'accepted' ? '#065f46' : l.price_type === 'approved' ? '#92400e' : '#6b7280' }}>{({ requested: 'R', approved: 'A', accepted: 'S', contract: 'C' })[l.price_type] || l.price_type[0]?.toUpperCase()}</span>}</td>
                      <td style={td}>{fmtPct(l.down_payment_pct)}</td>
                      <td style={td}>{fmt$(l.loan_amount)}</td>
                      <td style={{ ...td, color: ltvColor(l.ltv), fontWeight: '600' }}>{fmtPct(l.ltv)}</td>
                      <td style={{ ...td, color: dtiColor(l.dti_back), fontWeight: '600' }}>{fmtPct(l.dti_back)}</td>
                      <td style={td}>{(() => { const s = getCreditScore(l); return s.isActual ? <span style={{ fontWeight: '700', color: '#111827' }}>{s.display} <span style={{ fontSize: '10px', background: '#acf44e', color: '#1a1a1a', padding: '1px 5px', borderRadius: '4px', fontWeight: '700' }}>✓</span></span> : (l.fico_range || '—'); })()}</td>
                      <td style={td}>{fmtPct(l.interest_rate)}</td>
                      <td style={{ ...td, color: '#16a34a', fontWeight: '600' }}>{fmt$(l.re_commission)}</td>
                      <td style={{ ...td, color: '#16a34a', fontWeight: '600' }}>{fmt$(l.program_fee)}</td>
                      <td style={{ ...td, color: '#111827', fontWeight: '700' }}>{fmt$(l.total_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
                {loans.length > 0 && summary && (
                  <tfoot>
                    <tr>
                      <td style={tdTotal}>TOTALS / AVERAGES</td>
                      <td style={tdTotal} colSpan={4}></td>
                      <td style={tdTotal}>{fmt$(summary.total_doorly_price)}</td>
                      <td style={tdTotal}>{fmt$(summary.total_borrower_price)}</td>
                      <td style={tdTotal}>—</td>
                      <td style={tdTotal}>—</td>
                      <td style={tdTotal}>{fmt$(summary.total_loan_amount)}</td>
                      <td style={tdTotal}>{fmtPct(summary.avg_ltv)}</td>
                      <td style={tdTotal}>{fmtPct(summary.avg_dti_back)}</td>
                      <td style={tdTotal}>—</td>
                      <td style={tdTotal}>{fmtPct(summary.avg_interest_rate)}</td>
                      <td style={tdTotal}>{fmt$(summary.total_re_commission)}</td>
                      <td style={tdTotal}>{fmt$(summary.total_program_fees)}</td>
                      <td style={tdTotal}>{fmt$(summary.total_revenue)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
